import {
  Checkbox,
  Form,
  Input,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';

import {
  ConfirmDeletingElement,
  EditPageButtons,
  Error403Unauthorized,
} from '@app/components';
import { OFF, ONE_TIME_CODE, RULES_IS_REQUIRE } from '@app/constants';
import { ERROR } from '@app/constants/alerts';
import { PREFIX } from '@app/constants/appPrefix';
import { PAY_TYPES } from '@app/constants/payTypes';
import { addDataTestIdsToSelectDropdown } from '@app/utils';

import { useAddPaymentsAccount } from '@pages/Finances/AddPaymentAccounts/hooks/useAddPaymentsAccount';
import { usePaymentAccounts } from '@pages/Finances/PaymentAccountsList/hooks/usePaymentAccounts';

import type { FC } from 'react';

import '@pages/Finances/AddPaymentAccounts/styles/AddPaymentsAccount.desktop.scss';
import '@pages/Finances/AddPaymentAccounts/styles/AddPaymentsAccount.tablet.scss';

const { TextArea } = Input;

export const AddPaymentsAccount: FC = () => {
  const {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editablePaymentAccountId,
    handleCancel,
    handleDeletePaymentAccount,
    legalEntities,
    payType,
    modalOpen,
    setModalOpen,
    setInputData,
    setSelectorData,
    setCheckboxData,
    initialValues,
    handlePayTypeChange,
  } = useAddPaymentsAccount();
  const {
    hasUserRightToEditPaymentsAccount,
    hasUserRightToCreatePaymentsAccount,
    hasUserRightForModuleView,
  } = usePaymentAccounts();

  if (isLoading && editablePaymentAccountId) {
    return <Skeleton active />;
  }

  const isEditionPage = window.location.href.includes('/edit');
  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  } else if (
    (isEditionPage && !hasUserRightToEditPaymentsAccount) ||
    (!isEditionPage && !hasUserRightToCreatePaymentsAccount)
  ) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}add-payments-account`}>
      <Form
        form={form}
        onFinish={submitForm}
        className="form"
        autoComplete={OFF}
        initialValues={initialValues}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="name-payment-account-title"
            >
              Название
            </Typography.Text>

            <Form.Item name="Name" className="mg-0" rules={RULES_IS_REQUIRE}>
              <Input
                data-testid="name-payment-account-input"
                autoComplete={ONE_TIME_CODE}
                size="large"
                maxLength={300}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Name',
                    isEditPage: Boolean(editablePaymentAccountId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="legal-entity-title"
            >
              Юридическое лицо
            </Typography.Text>

            <Form.Item
              name="LegalEntityId"
              className="mg-0"
              rules={RULES_IS_REQUIRE}
            >
              <Select
                data-testid="legal-entity-select"
                size="large"
                options={legalEntities.map((legalEntity) => {
                  return {
                    label: legalEntity.Name,
                    value: legalEntity.Id,
                  };
                })}
                onChange={(value) => {
                  setSelectorData({ value, fieldName: 'LegalEntityId' });
                }}
                optionRender={(option) =>
                  addDataTestIdsToSelectDropdown({
                    form,
                    option,
                    itemDataTestId: 'legal-entity-item',
                    activeItemDataTestId: 'active-legal-entity',
                    formItemName: 'LegalEntityId',
                  })
                }
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label-require"
              strong
              data-testid="payment-method-title"
            >
              Метод оплаты
            </Typography.Text>

            <Form.Item name="PayType" className="mg-0" rules={RULES_IS_REQUIRE}>
              <Select
                data-testid="payment-method-select"
                size="large"
                options={PAY_TYPES}
                onChange={handlePayTypeChange}
                optionRender={(option) =>
                  addDataTestIdsToSelectDropdown({
                    form,
                    option,
                    itemDataTestId: 'payment-method-item',
                    activeItemDataTestId: 'active-payment-method',
                    formItemName: 'PayType',
                  })
                }
              />
            </Form.Item>
          </div>

          {payType === 'bank' ? (
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="bank-name-title"
              >
                Банк
              </Typography.Text>

              <Form.Item name="Bank" className="mg-0">
                <Input
                  data-testid="bank-name-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={200}
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'Bank',
                      isEditPage: Boolean(editablePaymentAccountId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>
          ) : (
            ''
          )}

          {payType === 'bank' ? (
            <div className="container">
              <Typography.Text className="label" strong data-testid="bik-title">
                БИК
              </Typography.Text>

              <Form.Item name="BankBik" className="mg-0">
                <Input
                  data-testid="bik-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={9}
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'BankBik',
                      isEditPage: Boolean(editablePaymentAccountId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>
          ) : (
            ''
          )}

          {payType === 'bank' ? (
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="correspondent-account-title"
              >
                Корреспондентский счет
              </Typography.Text>

              <Form.Item name="AccountCorrespondent" className="mg-0">
                <Input
                  data-testid="correspondent-account-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={20}
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'AccountCorrespondent',
                      isEditPage: Boolean(editablePaymentAccountId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>
          ) : (
            ''
          )}

          {payType === 'bank' ? (
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="checking-account-title"
              >
                Расчетный счет
              </Typography.Text>

              <Form.Item name="AccountChecking" className="mg-0">
                <Input
                  data-testid="checking-account-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={20}
                  onChange={(e) => {
                    setInputData({
                      event: e,
                      fieldName: 'AccountChecking',
                      isEditPage: Boolean(editablePaymentAccountId),
                      isTextField: true,
                    });
                  }}
                />
              </Form.Item>
            </div>
          ) : (
            ''
          )}

          <div className="container description-container">
            <Typography.Text className="label" strong data-testid="note-title">
              Примечание
            </Typography.Text>

            <Form.Item name="Description" className="mg-0">
              <TextArea
                data-testid="note-input"
                rows={4}
                autoComplete={ONE_TIME_CODE}
                maxLength={400}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Description',
                    isEditPage: Boolean(editablePaymentAccountId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="default-payment-account-title"
            >
              Касса по умолчанию
            </Typography.Text>

            <Form.Item
              name="IsDefault"
              className="mg-0"
              valuePropName="checked"
            >
              <Checkbox
                data-testid="default-payment-account-checkbox"
                onChange={(e) => {
                  setCheckboxData({
                    event: e,
                    fieldName: 'IsDefault',
                    isEditPage: Boolean(editablePaymentAccountId),
                  });
                }}
              >
                <span data-testid="default-payment-account-description">
                  Назначить кассой по умолчанию
                </span>
              </Checkbox>
            </Form.Item>
          </div>

          <EditPageButtons
            serverError={serverError}
            errorType={ERROR}
            setServerError={setServerError}
            isLoading={isLoading}
            setModalOpen={setModalOpen}
            handleCancel={handleCancel}
            isEditPage={Boolean(editablePaymentAccountId)}
          />
        </Space>
      </Form>

      <ConfirmDeletingElement
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={handleDeletePaymentAccount}
        content="Вы уверены, что хотите удалить кассу?"
        title="Удаление кассы"
        titleDataTestId="delete-payment-account-title"
        descriptionDataTestId="description-delete-payment-account"
        modalDataTestId="delete-payment-account-confirmation"
      />
    </div>
  );
};
